// Cable-tool well state machine (DESIGN §5). One call to tickDrilling per day.
//
// Well statuses: RIGGING_UP, DRILLING, PAUSED, TRIPPING, RUNNING_CASING, FISHING, CLEANOUT, GAS_BLOW,
//   GUSHER, AT_TD, COMPLETING, PRODUCING, SHUT_IN, DEPLETED, DRY, PLUGGED, SUSPENDED
// A DRILLING well can be blocked by well.problem = { type, sinceDay, text }:
//   NO_CREW | NO_ENGINE | NO_BIT | NO_LINE | FLOODED | MAX_DEPTH
import { wellsOnField, wellAdded, allWells } from './indexes.js';
import { chance, range, int, nextFloat } from '../core/rng.js';
import { eqDef, fieldDef, rigStats, bitStats, bitFitsRig, stat, MANPOWER_MULT, unitPrice, eraDollars, defsByCat, isAvailable, findDef, yf } from './defs.js';
import { strataAt, stratumAt, reservoirsAt } from './geo.js';
import { startTimer, cancelTimer } from './timers.js';
import { activeRigCrew, crewHas, traitCount, injureCrew } from './crew.js';
import { startProduction, tankCapacity } from './production.js';
import { clamp, notify, log, spend, nextId, fmt$ } from './util.js';
import { effects, playerHoldsPatent } from './research.js';
import { stockQty, takeStock, addStock } from '../core/state.js';
import { onWellTD } from './contracts.js';
// M2 rotary era (M2_SPEC §1–§6): rotary wells dispatch to these modules; cable-tool wells keep the M1 loop.
import { rotaryDrillDay, rotaryOpenHoleDay, stuckDay, onRotaryFishDone, isRotaryRig, ensureMud, rotaryRop } from './rotary.js';
import { onShutInDone, onKillDone, onBlowoutCapped, startM2Gusher } from './wellcontrol.js';
import { startCementing, onCementSet, checkCement, casingDay, bestCement, emitCement } from './cement.js';
import { returnString } from './pipe.js';
import { boilerPowerMult, boilerFuelMult } from './boiler.js'; // steam gauges: firing rate scales power & fuel
import { rigUpMult } from './automation.js';
import { allowableFn } from './m2/proration.js';
import { onReachKop, onLateralPay, lateralSidetrack, shaleComplete } from './m4/shale.js'; // M4-A: horizontal shale laterals (inert without well.lateral, i.e. before 2000)

const BUSY = new Set(['RIGGING_UP', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'GAS_BLOW', 'GUSHER', 'COMPLETING']);
export const ACTIVE_STATUSES = new Set(['RIGGING_UP', 'DRILLING', 'PAUSED', 'TRIPPING', 'RUNNING_CASING', 'FISHING', 'CLEANOUT', 'GAS_BLOW', 'GUSHER', 'COMPLETING']);

export const HARD_TUNE = 2; // hardF = HARD_TUNE/(1+h) — see CONTRACT changelog (tuned from 1/h)

// ---------------------------------------------------------------------------------------------
export function createWell(state, rig, fieldId, plotIdx, target) {
  const field = state.fields[fieldId];
  const plot = field.plots[plotIdx];
  const fdef = fieldDef(fieldId);
  const id = nextId(state, 'w');
  const n = wellsOnField(state, fieldId).length + 1;
  const rs = rigStats(eqDef(rig.defId));
  const well = {
    id, name: `${(state.company.name.split(' ').find((x) => x && !/^(the|a|an)$/i.test(x)) || 'Well')} #${n}`, fieldId, plot: plotIdx, // "The Eagle Oil Co." drills Eagle #1
    status: 'RIGGING_UP', spudDay: null, depth: 0,
    target: clamp(target ?? rs.maxDepth, 5, rs.maxDepth),
    rigId: rig.uid, bitUid: rig.bitUid, mud: { defId: null, ppg: 8.4 },
    casing: [], shoe: 0, openHoleDays: 0, problem: null,
    payFt: 0, payTop: null, reservoirId: null, revealed: [], stim: null, shots: 0,
    prod: null, gusher: null, cost: 0, history: [[state.day, 0]], passed: {}, gasSeen: {},
    timerId: null, fishTries: 0, security: { guards: [], fort: null }, lift: null,
    wob: defaultWob(state, rig), deviationDeg: 0, drilledBy: rig.defId ?? null,
    fdefName: fdef?.name,
    // M2 (rotary era) — harmless defaults on cable wells
    rigType: rs.rigType, rpm: 1.0, string: null, bypassed: [], eLogged: false, cored: [], contract: null,
    strikeDay: null, strikeRigCond: null, kick: null, knownPore: null, zonePore: {}, annular: null, waterCut: 0,
  };
  state.wells[id] = well;
  wellAdded(state); // perf index (indexes.js)
  plot.wellId = id;
  return well;
}

// Default weight-on-bit from the crew's temperament (DESIGN §14).
export function defaultWob(state, rig) {
  const crew = activeRigCrew(state, rig);
  if (traitCount(crew, 'reckless')) return 1.15;
  if (traitCount(crew, 'careful')) return 0.9;
  return 1.0;
}

export const WOB_MIN = 0.5, WOB_MAX = 1.6;

// WOB multipliers (DESIGN §14). Returns { ropMult, wearMult, riskMult, founder }.
// Where extra weight stops paying depends on the rock: soft ground takes a heavy hand, hard rock just eats the bit,
// and rock harder than the bit is rated for takes even less (driller's time shows this as the sweet-spot band).
export function hardFounderMult(h = 3, maxHard = 9) { return (h <= 2 ? 1.15 : h <= 4 ? 1 : h <= 6 ? 0.85 : 0.72) * (h > maxHard ? 0.85 : 1); }
// Driller's touch (driller's time, UI): today's share of time the player held weight in the formation's sweet spot
// (q) and spent bouncing the bit in hard rock (bounce). Set by setDriveTouchImpl for the day being driven only —
// absent in normal play.
// (the UI sets it just before the tick, and the tick advances state.day before drilling — so it's the day it was set or the next)
export function touchToday(state, well) { const t = well.touch; return t && (t.day === state.day || t.day === state.day - 1) ? t : null; }
export const touchRopMult = (t) => (t ? 1 + 0.15 * t.q : 1);
export const touchWearMult = (t) => (t ? (1 - 0.15 * t.q) * (1 + 1.2 * (t.bounce ?? 0)) : 1);
export function setDriveTouchImpl(state, wellId, q, bounce) {
  const w = state.wells[wellId];
  if (!w) return { ok: false, msg: 'No such well.' };
  w.touch = { day: state.day, q: clamp(+q || 0, 0, 1), bounce: clamp(+bounce || 0, 0, 1) };
  return { ok: true };
}
export function wobFactors(state, rig, wob, bitDef, h = 3, cf = null) {
  const bs = bitStats(bitDef);
  cf ??= crewFactors(state, rig);
  const drillerSkill = cf.crew.find((c) => c.role === 'driller')?.skill ?? 50;
  const founder = stat(bitDef, 'founderPoint', 1.25) * (1 + 0.1 * (drillerSkill - 50) / 50) * hardFounderMult(h, bs.maxHard);
  const effW = wob <= founder ? wob : founder + 0.3 * (wob - founder);
  const e = effects(state);
  const wearExp = 1.8 + e.wobWearExpDelta;
  return {
    founder,
    ropMult: Math.pow(effW, 0.9),
    wearMult: Math.pow(wob, wearExp) * (h > bs.maxHard ? 1.5 : 1),
    riskMult: 1 + 2.5 * Math.max(0, wob - 1.1) * e.highWobRiskMult,
  };
}

export function wellRig(state, well) { return well.rigId ? state.rigs[well.rigId] ?? null : null; }

export function setTask(state, well, kind, days, data = {}) {
  const rig = wellRig(state, well);
  const t = startTimer(state, kind, well.id, days, data);
  well.timerId = t.id;
  if (rig) rig.task = { type: kind, timerId: t.id, daysLeft: t.daysLeft };
  return t;
}

export function clearTask(state, well) {
  const rig = wellRig(state, well);
  well.timerId = null;
  if (rig) rig.task = null;
}

export function startRigUp(state, well, rig, days = null) {
  const rs = rigStats(eqDef(rig.defId));
  well.status = 'RIGGING_UP';
  well.rigType = rs.rigType;
  if (rs.rigType === 'rotary') {
    if (well.poreGuess == null) well.poreGuess = Math.round(range(state, -0.5, 0.5) * 100) / 100;
    well.rpm ??= 1.0;
    ensureMud(state, well);
  }
  setTask(state, well, 'RIG_UP', (days ?? rs.rigUpDays) * rigUpMult(state, rig));
}

// ---- ROP ------------------------------------------------------------------------------------
export function crewFactors(state, rig) {
  const rs = rigStats(eqDef(rig.defId));
  const crew = activeRigCrew(state, rig);
  const req = rs.requiredRoles ?? {};
  let missing = 0;
  for (const [role, n] of Object.entries(req)) {
    const have = crew.filter((c) => c.role === role).length;
    if (role !== 'driller') missing += Math.max(0, n - have);
  }
  const hasDriller = crew.some((c) => c.role === 'driller');
  const avgSkill = crew.length ? crew.reduce((a, c) => a + c.skill, 0) / crew.length : 0;
  const avgFatigue = crew.length ? crew.reduce((a, c) => a + c.fatigue, 0) / crew.length : 0;
  const crewF = (0.5 + avgSkill / 100) * (1 - avgFatigue / 200);
  const roleF = Math.pow(0.75, missing);
  const tourF = rig.tours >= 2 ? 1 : 0.5;
  const reckless = traitCount(crew, 'reckless');
  return { crew, hasDriller, missing, crewF, roleF, tourF, traitF: 1 + 0.1 * Math.min(2, reckless) };
}

export function enginePower(state, rig) {
  const rs = rigStats(eqDef(rig.defId));
  if (rig.engineUid && state.items[rig.engineUid]) return stat(eqDef(state.items[rig.engineUid].defId), 'powerMult', 1) * boilerPowerMult(state, rig);
  if (rs.needsEngine) return 0;
  return rs.powerMult ?? MANPOWER_MULT;
}

export function ropAt(state, well, s, cf = null) {
  if (well.rigType === 'rotary') return rotaryRop(state, well, s);
  const rig = wellRig(state, well);
  if (!rig) return 0;
  const rs = rigStats(eqDef(rig.defId));
  const bit = state.items[rig.bitUid];
  if (!bit) return 0;
  const bs = bitStats(eqDef(bit.defId));
  cf ??= crewFactors(state, rig);
  if (!cf.hasDriller) return 0;
  const h = Math.max(1, s.hard);
  const hardF = h <= bs.maxHard ? HARD_TUNE / (1 + h) : (0.2 * HARD_TUNE) / (1 + h);
  const bitCondF = bit.condition <= 0 ? 0.1 : 0.3 + 0.7 * (bit.condition / 100);
  const waterF = s.water && !waterSealed(well, s) ? 0.6 : 1; // bailing water
  const rigCondF = 0.7 + 0.3 * (rig.condition / 100);
  const wf = wobFactors(state, rig, well.wob ?? 1, eqDef(bit.defId), h, cf);
  const devF = (well.deviationDeg ?? 0) > 5 ? 0.8 : 1;
  return wf.ropMult * devF * effects(state).ropMult * rs.baseRop * enginePower(state, rig) * bs.speed * bitCondF * hardF * cf.crewF * cf.roleF * cf.tourF * cf.traitF * waterF * rigCondF;
}

const waterSealed = (well, s) => well.shoe > s.top;

// ---- daily step -----------------------------------------------------------------------------
const ROTARY_OPEN = new Set(['PAUSED', 'TRIPPING', 'AT_TD']);
export function tickDrilling(state) {
  const m2 = yf(state) >= 1901;
  for (const well of allWells(state)) {
    if (well.status === 'DRILLING') drillDay(state, well);
    else if (well.status === 'STUCK') stuckDay(state, well);
    else if (BUSY.has(well.status) || well.status === 'PAUSED') {
      well.openHoleDays++;
      if (well.rigType === 'rotary' && ROTARY_OPEN.has(well.status) && (well.rigId || well.contract)) rotaryOpenHoleDay(state, well);
    } else if (well.rigType === 'rotary' && well.status === 'AT_TD' && well.rigId) rotaryOpenHoleDay(state, well);
    if (m2 && well.casing?.length) casingDay(state, well);
    const rig = wellRig(state, well);
    if (rig?.task && state.timers[rig.task.timerId]) rig.task.daysLeft = state.timers[rig.task.timerId].daysLeft;
  }
}

export function setProblem(state, well, type, text, { pause = false, event = 'problem' } = {}) {
  if (well.problem?.type === type) return;
  well.problem = { type, sinceDay: state.day, text };
  notify(state, event, { wellId: well.id, fieldId: well.fieldId, text: `${well.name}: ${text}`, problem: type, pause, level: pause ? 'warn' : 'info' });
}

// Hands-on drilling (state.settings.handsOn — new games from the UI): a well only makes hole on days the player
// drove it on the brake (driller's time sets well.touch). Rig-up, trips, casing and the rest go on without you.
// Rigs with drilling automation fitted drill themselves. Sim-only games (tests, bots) leave the flag off.
export function waitingOnDriller(state, well, rig) {
  if (!state.settings?.handsOn || touchToday(state, well)) return false;
  if (rig && Object.values(rig.gear ?? {}).some((uid) => state.items[uid]?.defId === 'drilling_automation')) return false;
  setProblem(state, well, 'WAITING_DRILLER', 'waiting for you on the brake — open the well and drive it, or let crews drill unattended (Menu).');
  return true;
}

// The player just took the brake on this well: drop the 'waiting for you' flag now rather than at the end of the
// (slowed) day — the touch itself still only counts for the day it's driven.
export function takeBrakeImpl(state, wellId) {
  const w = state.wells?.[wellId];
  if (w?.problem?.type === 'WAITING_DRILLER') w.problem = null;
  return { ok: !!w };
}

// Is there casing on the market that can be run down to `depth`? (string casing, not per-job services)
function casingReaches(state, depth) {
  return defsByCat('casing').some((d) => !d.stats?.perWell && !d.stats?.perJob && d.stats?.wocDays == null && isAvailable(d, state) && !(d.obsoleteYear != null && yf(state) >= d.obsoleteYear) && (d.stats?.maxDepth ?? Infinity) >= depth);
}

export function drillDay(state, well) {
  if (well.rigType === 'rotary') return rotaryDrillDay(state, well);
  const rig = wellRig(state, well);
  if (!rig) { well.status = 'SUSPENDED'; return; }
  const fdef = fieldDef(well.fieldId);
  const plot = state.fields[well.fieldId].plots[well.plot];
  const strata = strataAt(fdef, plot);
  const rs = rigStats(eqDef(rig.defId));
  well.openHoleDays++;

  // --- blockers ---
  const cf = crewFactors(state, rig);
  if (!cf.hasDriller) return setProblem(state, well, 'NO_CREW', 'no driller on the rig — hire or assign one.', { pause: true });
  if (enginePower(state, rig) <= 0) return setProblem(state, well, 'NO_ENGINE', 'this rig needs a steam engine installed.', { pause: true });
  if (waitingOnDriller(state, well, rig)) return; // hands-on drilling: no hole made unless you're on the brake

  // water sand flooding (cable tool bails; once through the sand the hole fills until cased off)
  const flood = strata.find((s) => s.water && well.depth > s.top + 2 && !waterSealed(well, s));
  if (flood) {
    // before any casing can reach the water sand (1859–60: drive pipe only goes ~100 ft), crews bailed the water out
    // as they drilled — slow (ropAt's water factor), but the hole goes on. Once casing exists, the flood blocks as before.
    if (!casingReaches(state, flood.bottom)) {
      if (!well.bailingNoticed) { well.bailingNoticed = true; notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'BAILING', level: 'warn', text: `${well.name}: the water sand at ${Math.round(flood.top)} ft is flooding the hole and no casing made yet can reach it — the crew bails as they drill, slowly. Wrought-iron casing comes in 1861.` }); }
    } else {
      if (rig.auto.case && autoCase(state, well)) return;
      return setProblem(state, well, 'FLOODED', `hole flooded by the water sand at ${Math.round(flood.top)} ft — run casing to shut it off.`, { pause: true });
    }
  }
  // Unstable strata above us that we have passed: auto-case (drive pipe through gravel)
  if (rig.auto.case) {
    const loose = strata.find((s) => s.stableDays < 15 && well.depth >= s.bottom && well.shoe < s.bottom);
    if (loose && autoCase(state, well)) return;
    // any open stratum about to exceed its stable time → case it off while we can
    const aging = strata.find((s) => well.passed[s.idx] !== undefined && well.shoe < s.bottom && well.depth >= s.bottom && state.day - well.passed[s.idx] > s.stableDays * 0.8 && s.stableDays < 200);
    if (aging && autoCase(state, well)) return;
  }
  // collapse of open unstable strata
  for (const s of strata) {
    if (well.passed[s.idx] === undefined || well.shoe >= s.bottom) continue;
    const extra = state.day - well.passed[s.idx] - s.stableDays;
    if (extra <= 0) continue;
    const p = (s.hard <= 1 ? 0.03 + 0.01 * extra : 0.01 * (extra / 10)) * wobFactors(state, rig, well.wob ?? 1, eqDef(state.items[rig.bitUid]?.defId), s.hard, cf).riskMult;
    if (!state._rush && chance(state, Math.min(0.5, p))) return collapse(state, well, s);
  }

  // bit
  let bit = state.items[rig.bitUid];
  if (!bit || bit.condition <= rig.auto.tripBelowPct + 0.5) {
    if (bitService(state, well, rig)) return;
    bit = state.items[rig.bitUid];
    if (!bit || bit.condition <= 0) return setProblem(state, well, 'NO_BIT', 'no usable bit — buy one in the shop.', { pause: true, event: 'bitWorn' });
  }
  // line
  if (!ensureLine(state, rig, well.depth + 30)) {
    if (!rig.line || rig.line.ft <= well.depth + 1) return setProblem(state, well, 'NO_LINE', 'out of drilling cable — buy more rope.', { pause: true });
  }
  const lineLimit = rig.line ? Math.min(rig.line.ft, stat(eqDef(rig.line.defId), 'ratedDepth', 1500)) : 0;
  const depthLimit = Math.min(well.target, rs.maxDepth, lineLimit);
  if (well.depth >= depthLimit - 1e-6) {
    if (well.depth >= well.target - 1e-6 || well.depth >= rs.maxDepth - 1e-6) return reachTD(state, well);
    return setProblem(state, well, 'NO_LINE', `cable too short or rated too shallow for ${Math.round(well.target)} ft.`, { pause: true });
  }
  if ((well.deviationDeg ?? 0) > 10) {
    if (rig.auto.straighten !== false) { startStraighten(state, well, 'straighten'); return; }
    return setProblem(state, well, 'CROOKED', `hole is ${well.deviationDeg.toFixed(1)}° off vertical — straighten or sidetrack.`, { pause: true });
  }
  well.problem = null;

  // --- drill through strata piecewise ---
  let time = 1, drilled = 0, wear = 0, lineWear = 0;
  const bs = bitStats(eqDef(bit.defId));
  const touch = touchToday(state, well), tRop = touchRopMult(touch), tWear = touchWearMult(touch);
  const wob = well.wob ?? 1;
  well.minWob = Math.min(well.minWob ?? wob, wob);
  const oldHand = traitCount(cf.crew, 'old_hand') ? 0.8 : 1;
  const resList = reservoirsAt(fdef, plot);
  let event = null;
  // stop for a trip once the bit is dull (unless we're already running it to destruction)
  const wearBudget = bit.condition > rig.auto.tripBelowPct + 0.5 ? bit.condition - rig.auto.tripBelowPct : Infinity;
  while (time > 1e-6 && well.depth < depthLimit - 1e-6 && wear < wearBudget - 1e-9) {
    const s = stratumAt(strata, well.depth + 0.01);
    if (well.passed[s.idx] === undefined) {
      well.passed[s.idx] = state.day;
      if (s.idx >= 0 && isGasZone(s, resList) && !well.gasSeen[s.idx]) { well.gasSeen[s.idx] = true; event = { type: 'gas', s }; break; }
    }
    const rop = ropAt(state, well, s, cf) * tRop;
    if (rop <= 1e-6) break;
    let stop = Math.min(depthLimit, s.bottom > well.depth + 1e-6 ? s.bottom : Infinity); // below the last stratum: it extends
    const nextRes = resList.find((r) => !well.revealed.includes(r.id) && r.top < stop && r.top + r.thick > well.depth);
    if (nextRes && nextRes.top <= well.depth + 1e-6) { event = { type: 'pay', r: nextRes }; break; }
    if (nextRes) stop = nextRes.top + 0.5;
    const wfNow = wobFactors(state, rig, wob, eqDef(bit.defId), s.hard, cf);
    const wearPerFt = s.hard / (bs.life * effects(state).bitLifeMult) * 100 * (s.hard > bs.maxHard ? 3 : 1) * oldHand * wfNow.wearMult * tWear;
    const can = Math.min(rop * time, wearBudget === Infinity ? Infinity : (wearBudget - wear) / wearPerFt);
    const ft = Math.min(can, stop - well.depth);
    well.depth += ft;
    drilled += ft;
    time -= ft / rop;
    const wf = wobFactors(state, rig, wob, eqDef(bit.defId), s.hard, cf);
    const lifeMult = effects(state).bitLifeMult;
    wear += ft * s.hard / (bs.life * lifeMult) * 100 * (s.hard > bs.maxHard ? 3 : 1) * oldHand * wf.wearMult * tWear;
    lineWear += (ft / 1000) * Math.pow(wob, 1.8);
    well.deviationDeg = (well.deviationDeg ?? 0) + Math.max(0, wob - 1.2) * 0.4 * (ft / 100) * (s.dipping || s.dip ? 2 : 1);
    if (nextRes && well.depth >= nextRes.top) { event = { type: 'pay', r: nextRes }; break; }
  }
  well.depth = Math.round(well.depth * 100) / 100;
  // pay penetration while drilling inside the pay
  if (well.reservoirId) {
    const r = resList.find((x) => x.id === well.reservoirId);
    if (r) well.payFt = clamp(well.depth - r.top, 0, r.thick);
  }

  // driller's touch: a day spent bouncing the tools in hard rock can break the bit off in the hole
  if (touch) { well.touch = null; if (touch.bounce > 0.3 && drilled > 0 && chance(state, touch.bounce * 0.22)) { bit.condition = Math.max(0, bit.condition - 30); return startFishing(state, well, 'the bit snapped off bouncing in hard rock'); } }
  // --- wear, xp, costs ---
  if (drilled > 0) {
    bit.condition = Math.max(0, bit.condition - wear);
    if (rig.line) rig.line.condition = Math.max(0, rig.line.condition - lineWear * stat(eqDef(rig.line.defId), 'wearPer1000Ft', 1) - 0.02);
    rig.condition = Math.max(0, rig.condition - 0.02);
    state.company.stats.ftDrilled += drilled;
    for (const c of cf.crew) c.xp += drilled / 10;
    well.history.push([state.day, well.depth]);
    if (well.history.length > 400) well.history.splice(1, 1);
  }
  chargeFuel(state, rig, well);

  // --- events ---
  if (event?.type === 'gas') return gasBlow(state, well, rig, event.s);
  if (event?.type === 'pay') return hitPay(state, well, rig, event.r);

  const riskMult = wobFactors(state, rig, wob, eqDef(bit.defId), 3, cf).riskMult;
  well._fishFree = well._fishFree ?? true;
  // bit at 0: lost cone / broken bit risk
  if (!state._rush && bit.condition <= 0 && chance(state, 0.02 * riskMult)) return startFishing(state, well, 'the worn-out bit broke off in the hole');
  // rope snapping / lost tools
  const careful = traitCount(cf.crew, 'careful') ? 0.7 : 1;
  const reck = 1 + 0.4 * traitCount(cf.crew, 'reckless');
  let snapP = 0.0015 * careful * reck * riskMult;
  if (rig.line && rig.line.condition < 20) snapP += (20 - rig.line.condition) * 0.01 * riskMult;
  if (!state._rush && drilled > 0 && chance(state, snapP)) {
    if (rig.line) { rig.line.condition = Math.max(rig.line.condition, 0); rig.line.ft = Math.max(well.depth + 1, rig.line.ft * 0.85); }
    return startFishing(state, well, 'the cable snapped and the string of tools fell in the hole');
  }

  if (well.depth >= well.target - 1e-6 || well.depth >= rs.maxDepth - 1e-6) reachTD(state, well);
}

function isGasZone(s, resList) {
  if (s.water) return false;
  if (s.gas) return true;
  if ((s.pore ?? 8.6) > 8.6 && !resList.some((r) => r.top >= s.top && r.top < s.bottom)) return (s.pore ?? 8.6) > 8.8;
  return false;
}

export function chargeFuel(state, rig, well) {
  if (!rig.engineUid || !state.items[rig.engineUid]) return;
  const edef = eqDef(state.items[rig.engineUid].defId);
  const fuel = eraDollars(state, stat(edef, 'fuelPerDay', 0), edef.baseYear ?? edef.year ?? 1859) * boilerFuelMult(state, rig);
  if (fuel > 0) { spend(state, fuel, 'fuel', false); if (well) well.cost += fuel; }
}

// Line (cable) bookkeeping: rig.line = { defId, ft, condition }. Auto-loads rope from stock.
export function ensureLine(state, rig, needFt) {
  const lineDefs = defsByCat('line').filter((d) => stockQty(state, d.id) > 0);
  if (!rig.line) {
    const d = lineDefs.sort((a, b) => stat(b, 'ratedDepth', 0) - stat(a, 'ratedDepth', 0))[0];
    if (!d) return false;
    const take = Math.min(stockQty(state, d.id), Math.max(needFt, 100));
    takeStock(state, d.id, take);
    rig.line = { defId: d.id, ft: take, condition: 100 };
  }
  if (rig.line.ft >= needFt) {
    if (rig.auto.rerope && rig.line.condition < 25 && stockQty(state, rig.line.defId) >= rig.line.ft) {
      takeStock(state, rig.line.defId, rig.line.ft);
      rig.line.condition = 100;
      log(state, `${rig.name}: re-roped with fresh cable.`);
    }
    return true;
  }
  const more = Math.min(stockQty(state, rig.line.defId), needFt - rig.line.ft);
  if (more > 0) { takeStock(state, rig.line.defId, more); rig.line.ft += more; }
  return rig.line.ft >= needFt;
}

// Auto-trip: dress the bit if possible, else change to the best spare. Returns true if a task started.
function bitService(state, well, rig) {
  const bit = state.items[rig.bitUid];
  const cf = crewFactors(state, rig);
  if (bit && rig.auto.dress && bit.maxCondition - 15 >= 25 && bit.condition < bit.maxCondition - 15) {
    startDress(state, well, rig, cf);
    return true;
  }
  const spare = bestSpareBit(state, rig);
  if (spare) { startBitChange(state, well, rig, spare.uid); return true; }
  if (bit && bit.condition > 0) { // keep drilling on the dull bit — and say so once per bit
    if (!bit.dullWarned) { bit.dullWarned = true; notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'DULL_BIT', level: 'warn', text: `${well.name}: the bit is dull (${Math.round(bit.condition)}%) and too worn to dress again, with no spare on the rack — the crew is running it to destruction, slowly. Buy a bit.` }); }
    return false;
  }
  if (bit && bit.maxCondition - 15 >= 10) { startDress(state, well, rig, cf); return true; }
  return false;
}

export function startDress(state, well, rig, cf = crewFactors(state, rig)) {
  const hasDresser = cf.crew.some((c) => c.role === 'tool_dresser');
  well.status = 'TRIPPING';
  setTask(state, well, 'DRESS', hasDresser ? 1 : 2);
  log(state, `${well.name}: pulling tools to dress the bit${hasDresser ? '' : ' (no tool dresser — driller does it, 2 days)'}.`, 'info', { wellId: well.id });
}

export function startBitChange(state, well, rig, bitUid, days = null) {
  well.status = 'TRIPPING';
  setTask(state, well, 'TRIP', days ?? Math.max(1, (2 * well.depth) / 10000), { bitUid });
}

export function bestSpareBit(state, rig) {
  const rdef = eqDef(rig.defId);
  const spares = Object.values(state.items).filter((i) => i.loc.type === 'warehouse' && eqDef(i.defId)?.cat === 'bit' && bitFitsRig(eqDef(i.defId), rdef) && i.condition > 20 && (i.bearing ?? 100) > 30);
  if (rig._preferBit) { const p = spares.find((i) => i.defId === rig._preferBit); if (p) return p; }
  spares.sort((a, b) => bitStats(eqDef(b.defId)).speed * b.condition - bitStats(eqDef(a.defId)).speed * a.condition);
  return spares[0] ?? null;
}

// Pick a casing def & run it (auto). Returns true if casing started.
export function autoCase(state, well) {
  if (well._autoCaseFailDay === state.day) return false;
  const ft = well.depth - well.shoe;
  const defs = defsByCat('casing').filter((d) => !stat(d, 'perWell', false) && isAvailable(d, state));
  defs.sort((a, b) => (stockQty(state, b.id) >= ft) - (stockQty(state, a.id) >= ft) || unitPrice(state, a) - unitPrice(state, b));
  // rotary era: cement the string when a cement service exists and the rig's auto.cement is on (M2_SPEC §3)
  const rig = wellRig(state, well);
  const cem = well.rigType === 'rotary' && (rig?.auto?.cement ?? true) ? bestCement(state)?.id ?? null : null;
  for (const c of cem ? [cem, null] : [null]) {
    for (const d of defs) {
      const r = runCasingImpl(state, well, d.id, { cement: c });
      if (r.ok) {
        if (cem && !c) { const t = state.timers[well.timerId]; emitCement(state, well, false, { bottom: t?.data?.bottom ?? well.depth }, cem, 'not enough cash for the cement job'); }
        return true;
      }
    }
  }
  well._autoCaseFailDay = state.day;
  return false;
}

export function runCasingImpl(state, well, defId, opts = {}) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'casing' || stat(def, 'perWell', false) || stat(def, 'perJob', false)) return { ok: false, msg: 'Not a casing string.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} is not available until ${def.year}.` };
  const rig = wellRig(state, well);
  if (!rig && !well.contract) return { ok: false, msg: 'Casing needs a rig on the well.' };
  if (well.rigType === 'rotary' && (well.casing?.length ?? 0) >= 4) return { ok: false, msg: 'Four strings already: conductor, surface, intermediate and production.' };
  const cem = checkCement(state, opts.cement ?? null);
  if (!cem.ok) return cem;
  if (!['DRILLING', 'PAUSED', 'AT_TD', 'DRY'].includes(well.status)) return { ok: false, msg: `Can't run casing while ${well.status}.` };
  const ft = Math.ceil(well.depth - well.shoe);
  if (ft < 1) return { ok: false, msg: 'Hole is already cased to bottom.' };
  const maxD = stat(def, 'maxDepth', null);
  if (maxD && well.depth > maxD) return { ok: false, msg: `${def.name} only goes to ${maxD} ft.` };
  const have = stockQty(state, defId);
  const buy = Math.max(0, ft - have);
  const price = buy * unitPrice(state, def);
  // seed-bag packer (perWell casing item) when available — cheap, auto-bought
  const packer = defsByCat('casing').find((d) => stat(d, 'perWell', false) && isAvailable(d, state));
  const packerCost = packer && def.year > 1859 && !well.casing.length ? (stockQty(state, packer.id) > 0 ? 0 : unitPrice(state, packer)) : 0;
  if (price + packerCost + cem.price > state.company.cash) return { ok: false, msg: `Need ${ft} ft of ${def.name}${cem.def ? ` and a ${cem.def.name}` : ''} (${fmt$(price + packerCost + cem.price)}); not enough cash.` };
  if (buy > 0) { spend(state, price, 'consumables'); addStock(state, defId, buy); }
  if (packerCost > 0) spend(state, packerCost, 'consumables');
  if (cem.price > 0) { spend(state, cem.price, 'consumables'); well.cost += cem.price; }
  else if (packer && def.year > 1859 && !well.casing.length) takeStock(state, packer.id, 1);
  takeStock(state, defId, ft);
  well.cost += ft * unitPrice(state, def) + packerCost;
  well.status = 'RUNNING_CASING';
  well._resumeStatus = well.problem?.type === 'FLOODED' || well.status === 'DRILLING' ? 'DRILLING' : null;
  setTask(state, well, 'CASING', Math.max(1, ft / 1000), { defId, top: well.shoe, bottom: well.depth, resume: well.depth < well.target ? 'DRILLING' : 'AT_TD', cement: cem.def?.id ?? null });
  log(state, `${well.name}: running ${ft} ft of ${def.name}${cem.def ? ` and cementing it (${cem.def.name})` : ''}.`, 'info', { wellId: well.id });
  return { ok: true, msg: `Running ${ft} ft of ${def.name}${cem.def ? ` + ${cem.def.name}` : ''}.` };
}

export function collapse(state, well, s) {
  const cavedTo = Math.max(well.shoe, s.top);
  well.passed[s.idx] = state.day; // bailed & reamed: the clock restarts
  if (chance(state, 0.3)) return startFishing(state, well, `the ${s.rock} caved in on the tools`);
  well.status = 'CLEANOUT';
  setTask(state, well, 'CLEANOUT', int(state, 2, 5));
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'COLLAPSE', text: `${well.name}: the open ${s.rock} at ${Math.round(cavedTo)} ft caved in. Bailing it out — case unstable ground!`, level: 'warn' });
}

export function startFishing(state, well, why) {
  well.status = 'FISHING';
  well._fishFree = false;
  well.fishTries = (well.fishTries ?? 0) + 1;
  const jars = effects(state).fishingTimeMult;
  well.fishingJobs = (well.fishingJobs ?? 0) + 1;
  const t = setTask(state, well, 'FISHING', int(state, 3, 10) * jars, { why });
  spend(state, eraDollars(state, 25), 'other', false);
  well.cost += eraDollars(state, 25);
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'FISHING', pause: true, text: `${well.name}: ${why}! Fishing for the tools (~${t.daysLeft} days).`, level: 'warn' });
}

// dev mode: a gas blow from the stratum the bit is in
export function gasBlowDev(state, well, rig) {
  const strata = fieldDef(well.fieldId)?.strata ?? [];
  const s = strata.find((x) => well.depth >= x.top && well.depth < x.bottom) ?? strata.at(-1) ?? { rock: 'sand' };
  return gasBlow(state, well, rig, s);
}
function gasBlow(state, well, rig, s) {
  well.status = 'GAS_BLOW';
  setTask(state, well, 'GAS_BLOW', int(state, 1, 3));
  let text = `${well.name}: gas blow from the ${s.rock} at ${Math.round(well.depth)} ft — work stops until it bleeds off.`;
  if (!state._rush && chance(state, 0.05)) {
    const dmg = range(state, 20, 50);
    rig.condition = Math.max(0, rig.condition - dmg);
    well.fire = { day: state.day, untilDay: state.day + 3 };
    text = `${well.name}: gas blow caught FIRE at ${Math.round(well.depth)} ft! Rig damaged ${Math.round(dmg)}%.`;
    const crew = activeRigCrew(state, rig);
    if (crew.length && chance(state, 0.4)) injureCrew(state, crew[Math.floor(nextFloat(state) * crew.length)], 'was burned in a gas fire', { cause: 'well fire', fatalChance: 0.2, wellId: well.id, accident: 'well_fire' });
    state.company.reputation = Math.max(0, state.company.reputation - 2);
    return notify(state, 'blowout', { wellId: well.id, fieldId: well.fieldId, text, pause: true, level: 'warn' });
  }
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'GAS', text });
}

export function hitPay(state, well, rig, r, opts = {}) {
  well.revealed.push(r.id);
  if (well.lateral && onLateralPay(state, well, r)) return; // M4-A: benches above the landing zone are shows
  if (well.reservoirId) return; // already producing zone above; keep drilling
  if (well.deepening) { well.deepening = null; well.prod = null; } // deepened into a new sand: the old zone is left behind pipe
  well.reservoirId = r.id;
  well.payTop = r.top;
  well.strikeDay ??= state.day;
  well.strikeRigCond ??= rig?.condition ?? 100;
  const field = state.fields[well.fieldId];
  const res = field.reservoirs[r.id];
  state.company.stats.strikes++;
  state.company.stats.dryStreak = 0;
  state.company.reputation = Math.min(100, state.company.reputation + 3);
  for (const c of rig ? activeRigCrew(state, rig) : []) c.morale = Math.min(100, c.morale + 10);
  bumpNeighbors(state, well.fieldId, well.plot);
  const pressure = res ? res.pressure : 1;
  // Rotary wells gush only through a kick (mud below pore, mud.js); cable tools roll the M1 gusher chance.
  if (!opts.rotary && chance(state, (r.gusherChance ?? 0.03) * pressure * (res && res.remaining > 0 ? 1 : 0))) {
    if (yf(state) >= 1901) return startM2Gusher(state, well, rig, r);
    return startGusher(state, well, rig, r);
  }
  notify(state, 'strike', { wellId: well.id, fieldId: well.fieldId, pause: true, level: 'major', text: `Pay sand! ${well.name} struck oil at ${Math.round(r.top)} ft in the ${r.name ?? r.id.replace(/_/g, ' ')}.` });
  const maxD = rig ? rigStats(eqDef(rig.defId)).maxDepth : (well.contract?.maxDepth ?? well.target);
  if (rig ? rig.auto.drillThroughPay : true) well.target = Math.min(r.top + r.thick, maxD);
  else well.target = well.depth;
  if (well.depth >= well.target - 1e-6) reachTD(state, well);
}

function startGusher(state, well, rig, r) {
  const qMax = (r.qiRange ?? [10, 60])[1];
  const qi = qMax * range(state, 1.5, 6);
  well.status = 'GUSHER';
  well.gushed = true;
  well.payFt = r.thick;
  well.gusher = { rate: Math.round(qi * 3), qiAfter: Math.round(qi), startDay: state.day, captured: 0, lost: 0 };
  setTask(state, well, 'CAPPING', int(state, 3, 15));
  state.company.stats.gushers++;
  const tank = tankCapacity(state, well.fieldId) > 0;
  notify(state, 'gusher', { wellId: well.id, fieldId: well.fieldId, pause: true, level: 'major', text: `GUSHER! ${well.name} blew in at ${Math.round(r.top)} ft, roaring ~${Math.round(qi * 3).toLocaleString('en-US')} bbl/d over the crown block! ${tank ? 'Your tank is catching it.' : 'Without a tank only ~30% can be caught in pits.'}` });
}

// Lease prices of plots around a strike jump 30–100%.
export function bumpNeighbors(state, fieldId, plotIdx) {
  const field = state.fields[fieldId];
  const p0 = field.plots[plotIdx];
  for (const p of field.plots) {
    const d = Math.max(Math.abs(p.x - p0.x), Math.abs(p.y - p0.y));
    if (d === 1 && !p.owned) {
      p.priceMult = Math.min(8, p.priceMult * (1 + range(state, 0.3, 1.0)));
      p.pubEst = Math.min(0.95, p.pubEst + 0.2);
    } else if (d === 2 && !p.owned) p.priceMult = Math.min(8, p.priceMult * 1.15);
  }
}

export function reachTD(state, well) {
  if (well.ownerOperatorId) return onWellTD(state, well); // contract well: hand over to the operator (§17)
  if (well.lateral && onReachKop(state, well)) return; // M4-A: at the kickoff point — build the curve
  const rig = wellRig(state, well);
  if (well.contract && !well.contract.doneDay) well.contract.doneDay = state.day; // contractor rigs down at TD
  well.stopAt = null;
  if (!well.reservoirId && well.deepening) {
    endDeepening(state, well);
    notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'DEEPEN_DRY', text: `${well.name}: no deeper pay down to ${Math.round(well.depth)} ft — back on the old sand.`, level: 'info' });
    if (rig) releaseRigImpl(state, rig);
    return;
  }
  if (well.reservoirId) {
    well.status = 'AT_TD';
    well.problem = null;
    if (rig?.auto.complete) completeImpl(state, well);
    return;
  }
  well.status = 'DRY';
  well.problem = null;
  state.company.stats.dryHoles++;
  state.company.stats.dryStreak++;
  notify(state, 'dry', { wellId: well.id, fieldId: well.fieldId, pause: true, level: 'major', text: `Dry hole. ${well.name} reached ${Math.round(well.depth)} ft without pay. Deepen or plug it.` });
}

export function completeImpl(state, well) {
  if (well.ownerOperatorId) { onWellTD(state, well); return { ok: true, msg: `${well.name} handed over to the operator.` }; }
  if (well.status !== 'AT_TD') return { ok: false, msg: 'Well must be at TD with pay to complete.' };
  if (well.lateral) return shaleComplete(state, well); // M4-A: a horizontal shale well is completed by a frac job
  if (!well.reservoirId) return { ok: false, msg: 'No pay zone to complete.' };
  well.status = 'COMPLETING';
  setTask(state, well, 'COMPLETION', 1);
  return { ok: true, msg: 'Completing the well.' };
}

// Timer completions for rig/well tasks.
export function onDrillTimerDone(state, t, rushed) {
  const well = state.wells[t.refId];
  if (!well) return;
  clearTask(state, well);
  const rig = wellRig(state, well);
  switch (t.kind) {
    // ---- M2 rotary era ----
    case 'SHUT_IN': return onShutInDone(state, well, t);
    case 'KILL': return onKillDone(state, well, t);
    case 'CEMENTING': {
      well.status = onCementSet(state, well, t);
      if (well.status === 'AT_TD' && (rig?.auto.complete ?? true) && well.reservoirId) completeImpl(state, well);
      return;
    }
    case 'RECOMPLETE': startProduction(state, well); return;
    case 'CONTRACT_MOB':
      well.status = 'DRILLING';
      if (well.spudDay == null) { well.spudDay = state.day; state.company.stats.wellsDrilled++; state.fields[well.fieldId].plots[well.plot].leaseExpires = null; log(state, `${well.name} spudded by the drilling contractor.`, 'info', { wellId: well.id }); }
      return;
    case 'RIG_UP':
      well.status = 'DRILLING';
      if (well.spudDay == null) {
        well.spudDay = state.day;
        state.company.stats.wellsDrilled++;
        const plot = state.fields[well.fieldId].plots[well.plot];
        plot.leaseExpires = null; // lease held by drilling
        log(state, `${well.name} spudded.`, 'info', { wellId: well.id });
      }
      break;
    case 'DRESS': {
      const bit = rig && state.items[rig.bitUid];
      if (bit) { bit.maxCondition = Math.max(0, bit.maxCondition - 15); bit.condition = bit.maxCondition; }
      state.company.stats.bitDressings = (state.company.stats.bitDressings ?? 0) + 1;
      well.status = 'DRILLING';
      break;
    }
    case 'TRIP': {
      const nb0 = state.items[t.data.bitUid];
      // another rig may have taken that spare during the trip: then keep running the old bit
      if (rig && nb0 && (nb0.loc.type === 'warehouse' || nb0.loc.id === rig.uid)) {
        const old = state.items[rig.bitUid];
        const rotaryBit = old && isRotaryRig(rig) && !stat(eqDef(old.defId), 'dressable', false);
        if (old && rotaryBit && (old.condition < 30 || (old.bearing ?? 100) < 30)) delete state.items[old.uid]; // rotary bits aren't dressed
        else if (old) {
          if (old.maxCondition - 15 < 25) delete state.items[old.uid]; // worn past dressing: scrap iron
          else {
            old.loc = { type: 'warehouse', id: null };
            // the tool dresser sharpens it at the forge while the new one runs
            if (crewFactors(state, rig).crew.some((c) => c.role === 'tool_dresser')) { old.maxCondition -= 15; old.condition = old.maxCondition; }
          }
        }
        const nb = nb0;
        { nb.loc = { type: 'rig', id: rig.uid }; rig.bitUid = nb.uid; well.bitUid = nb.uid; state.company.stats.bitChanges = (state.company.stats.bitChanges ?? 0) + 1; if (stat(eqDef(nb.defId), 'bearingDays', null) && nb.bearing == null) nb.bearing = 100; }
      }
      well.status = 'DRILLING';
      break;
    }
    case 'CASING':
      well.casing.push({ defId: t.data.defId, top: t.data.top, bottom: t.data.bottom, cemented: false, day: state.day });
      well.shoe = Math.max(well.shoe, t.data.bottom);
      well.openHoleDays = 0;
      if (well.problem?.type === 'FLOODED') well.problem = null;
      if (t.data.cement) {
        const resume = well.reservoirId && well.depth >= well.target - 1e-6 ? 'AT_TD' : well.depth >= well.target - 1e-6 ? 'DRY' : 'DRILLING';
        if (startCementing(state, well, t.data.cement, resume, setTask)) break;
      }
      well.status = well.reservoirId && well.depth >= well.target - 1e-6 ? 'AT_TD' : well.depth >= well.target - 1e-6 ? 'DRY' : 'DRILLING';
      if (well.status === 'AT_TD' && rig?.auto.complete) completeImpl(state, well);
      break;
    case 'FISHING': {
      if (well.rigType === 'rotary' && onRotaryFishDone(state, well, t, rushed)) break;
      const cf = rig ? crewFactors(state, rig) : null;
      const skill = cf?.crew.find((c) => c.role === 'driller')?.skill ?? 30;
      if (rushed || chance(state, 0.7 + skill / 500)) {
        notify(state, 'problem', { wellId: well.id, problem: 'FISHED', text: `${well.name}: tools recovered — back to drilling.` });
        well.fishTries = 0;
        well.status = 'DRILLING';
      } else if (well.fishTries < 3) {
        startFishing(state, well, 'the fishing jars slipped off');
      } else {
        // abandon the tools: lose the bit, skid past the junk (lose some hole)
        if (rig && state.items[rig.bitUid]) { delete state.items[rig.bitUid]; rig.bitUid = null; well.bitUid = null; }
        well.depth = Math.max(well.shoe, well.depth - 20);
        well.fishTries = 0;
        well.status = 'DRILLING';
        notify(state, 'problem', { wellId: well.id, problem: 'JUNKED', pause: true, text: `${well.name}: gave up fishing. Bit lost; 20 ft of hole junked.` });
        if (rig) { const spare = bestSpareBit(state, rig); if (spare) startBitChange(state, well, rig, spare.uid); }
      }
      break;
    }
    case 'CLEANOUT':
    case 'GAS_BLOW':
      well.status = 'DRILLING';
      break;
    case 'STRAIGHTEN':
      if (well.lateral && lateralSidetrack(state, well, t)) break; // M4-A: a sidetrack in the lateral loses lateral footage
      if (t.data.method === 'sidetrack') { well.depth = Math.max(well.shoe, well.depth - (t.data.lossFt ?? 50)); well.deviationDeg = 1; }
      else well.deviationDeg = 2;
      well.status = 'DRILLING';
      well.problem = null;
      break;
    case 'CAPPING': {
      if (well.status === 'BLOWOUT' || t.data?.blowout) { onBlowoutCapped(state, well, rushed); break; }
      const g = well.gusher;
      notify(state, 'gusher', { wellId: well.id, fieldId: well.fieldId, capped: true, text: `${well.name}: gusher brought under control${rushed ? ' early' : ''}. Caught ${Math.round(g?.captured ?? 0).toLocaleString('en-US')} bbl, lost ${Math.round(g?.lost ?? 0).toLocaleString('en-US')} bbl.` });
      well.status = 'AT_TD';
      well.target = Math.min(well.target, well.depth);
      completeImpl(state, well);
      break;
    }
    case 'COMPLETION':
      startProduction(state, well);
      // rig is free again (unless something else needs it)
      if (rig && !well.lift?.usesRig) releaseRigImpl(state, rig);
      break;
    default:
      break;
  }
}

// Deepening a completed well (a bigger rig drills on below the producing sand).
// The old zone's numbers are parked on well.deepening; if no new sand turns up the well goes back on it as it was.
export function beginDeepening(state, well) {
  well.deepening = { reservoirId: well.reservoirId, status: well.status, payFt: well.payFt, payTop: well.payTop, gusher: well.gusher ?? null, lift: well.lift ?? null };
  well.reservoirId = null; well.payFt = 0; well.payTop = null; well.gusher = null; well.lift = null;
  well.status = 'SUSPENDED';
}
export function endDeepening(state, well) {
  const d = well.deepening; if (!d) return;
  well.reservoirId = d.reservoirId; well.payFt = d.payFt; well.payTop = d.payTop; well.gusher = d.gusher;
  well.lift = d.lift?.usesRig ? null : d.lift;
  well.status = d.status === 'SHUT_IN' ? 'SHUT_IN' : d.status === 'DEPLETED' ? 'DEPLETED' : 'PRODUCING';
  well.deepening = null; well.problem = null;
  if (well.timerId) { cancelTimer(state, well.timerId); well.timerId = null; }
}

export function releaseRigImpl(state, rig) {
  const well = rig.wellId ? state.wells[rig.wellId] : null;
  if (well?.deepening && !well.reservoirId) { // rig pulled off before finding anything deeper: back on the old sand
    well.rigId = null; rig.at = null; rig.wellId = null; rig.task = null;
    if (well.string) returnString(state, well);
    endDeepening(state, well);
    return;
  }
  if (well) {
    if (well.timerId) { cancelTimer(state, well.timerId); well.timerId = null; }
    if (ACTIVE_STATUSES.has(well.status) && well.status !== 'GUSHER') well.status = 'SUSPENDED';
    if (well.lift?.usesRig) well.lift = null;
    if (well.string) returnString(state, well);
    well.rigId = null;
    if (well.problem?.type === 'WAITING_DRILLER') well.problem = null; // no rig, nothing to wait for
  }
  rig.at = null;
  rig.wellId = null;
  rig.task = null;
}

// Crooked hole fix: 'straighten' (ream with a straight bit, 3–6 d) or 'sidetrack' (skid past, lose 50 ft, 2 d).
export function startStraighten(state, well, method = 'straighten') {
  well.status = 'TRIPPING';
  const days = method === 'sidetrack' ? 2 : int(state, 3, 6);
  setTask(state, well, 'STRAIGHTEN', days, { method });
  notify(state, 'problem', { wellId: well.id, fieldId: well.fieldId, problem: 'CROOKED', text: `${well.name}: hole drifted ${well.deviationDeg.toFixed(1)}° off vertical — ${method === 'sidetrack' ? 'sidetracking' : 'reaming it straight'} (${days} d). Ease off the weight!`, level: 'warn' });
  return { ok: true, msg: `${method === 'sidetrack' ? 'Sidetracking' : 'Straightening'} the hole (${days} days).` };
}

export function plugImpl(state, well) {
  const cost = eraDollars(state, 0.5) * well.depth;
  spend(state, cost, 'other', false);
  well.cost += cost;
  if (well.timerId) cancelTimer(state, well.timerId);
  well.timerId = null;
  well.status = 'PLUGGED';
  if (well.prod) well.prod.rate = 0;
  const rig = wellRig(state, well);
  if (rig) releaseRigImpl(state, rig);
  return { ok: true, msg: `${well.name} plugged (${fmt$(cost)}).` };
}

// What a stimulation job costs right now: the item (free if one's in the warehouse) plus Roberts' patent fee
// (era dollars) unless you hold the patent. Shared by shootImpl and the Well panel's buttons.
export function stimCostOf(state, defId) {
  const def = eqDef(defId);
  if (!def) return { price: 0, fee: 0, total: 0, inStock: false };
  const fee = playerHoldsPatent(state, 'roberts_torpedo') ? 0 : eraDollars(state, stat(def, 'patentFee', 0), def.baseYear ?? def.year);
  const inStock = stockQty(state, defId) > 0, price = inStock ? 0 : unitPrice(state, def);
  return { price, fee, total: price + fee, inStock };
}

// From 2000 (M4) the job has to suit the well, as the Well panel's list does: multi-stage systems and refracs go
// through the shale completion, SAGD pairs only on SAGD leases, no legacy jobs on a lateral, acid needs carbonate
// pay, massive fracs need tight rock, gravel packs need sand. Earlier eras keep the old free-for-all (saves/goldens).
const CARBONATE_ROCK = /lime|dolo|chalk|carbon|reef/;
export function stimFitReason(state, well, def) {
  if (yf(state) < 2000) return null;
  const st = def.stats ?? {};
  if (st.perStage || st.legacyPricing) return `${def.name} goes into a horizontal well's completion design, not a one-off job.`;
  if (st.refrac) return 'Refracs are ordered from the Shale well card (Refrac).';
  const fd = fieldDef(well.fieldId);
  if (st.sagd) return fd?.oilSands?.extraction === 'sagd' ? null : 'SAGD well pairs are for steam-assisted oil-sands leases.';
  if (well.lateral) return 'Horizontal wells are stimulated by their frac design or a refrac.';
  const plot = state.fields[well.fieldId]?.plots?.[well.plot];
  const r = plot ? reservoirsAt(fd, plot).find((x) => x.id === well.reservoirId) : null;
  const top = r?.top ?? well.depth - 5;
  const s = plot ? strataAt(fd, plot).find((x) => top >= x.top && top < x.bottom) : null;
  const rock = s?.rock ? String(s.rock).toLowerCase() : null;
  if (st.rockOnly && rock && !(st.rockOnly === 'limestone' ? CARBONATE_ROCK.test(rock) : rock.includes(st.rockOnly))) return `${def.name} needs ${st.rockOnly === 'limestone' ? 'carbonate' : st.rockOnly} pay; this well produces from ${rock}.`;
  if (st.tightOnly && !(r?.unconventional || /tight|shale|chalk/i.test(`${r?.name ?? ''} ${fd?.play ?? ''}`))) return `${def.name} is for tight rock; this pay flows without it.`;
  if (st.sandControl && ((rock && !rock.includes('sand')) || r?.unconventional)) return `${def.name} holds back loose sand; this pay isn't sand.`;
  return null;
}

// Torpedo shot (nitroglycerine): qi ×2–4, accident & lawsuit risk.
export function shootImpl(state, well, defId) {
  const def = eqDef(defId);
  if (!def || def.cat !== 'stim') return { ok: false, msg: 'Not a stimulation item.' };
  if (!isAvailable(def, state)) return { ok: false, msg: `${def.name} not available until ${def.year}.` };
  if (!well.reservoirId || !['AT_TD', 'PRODUCING', 'SHUT_IN'].includes(well.status)) return { ok: false, msg: 'Torpedoes go into a completed pay zone.' };
  const unfit = stimFitReason(state, well, def);
  if (unfit) return { ok: false, msg: unfit };
  // Roberts' patent: you pay his fee unless you patented the torpedo yourself (DESIGN §13.3)
  const ownPatent = playerHoldsPatent(state, 'roberts_torpedo'); // no lawsuit risk on your own patent (below)
  const { price, fee } = stimCostOf(state, defId);
  if (price + fee > state.company.cash) return { ok: false, msg: `Need ${fmt$(price + fee)}.` };
  if (price === 0) takeStock(state, defId, 1); else spend(state, price, 'consumables');
  if (fee > 0) spend(state, fee, 'other');
  well.cost += price + fee;
  state.company.stats.torpedoes++;
  const [lo, hi] = stat(def, 'qiMultRange', [2, 4]);
  const raw = range(state, lo, hi);
  const mult = 1 + (raw - 1) / (1 + well.shots);
  well.shots++;
  const msgs = [];
  let capNote = '';
  if (well.prod) {
    // Boost the well's decline-curve rate (before depletion sharing, lift and caps are applied each day), not the
    // capped output: using the output double-counted depletion and could even lower qi.
    const p = well.prod, curve = p.qi / Math.pow(1 + p.b * p.di * (p.age / 365), 1 / p.b);
    p.qi = Math.max(curve, 1) * mult;
    p.age = 0;
    // under prorationing the regulator's allowable, not the potential, sets output: say what the well may actually make
    if (p.allowable != null && p.potential != null) {
      const pot = p.potential * mult, allow = allowableFn(state, well.fieldId)(pot);
      if (allow < pot * 0.97) capNote = ` Its potential is now about ${Math.round(pot)} bbl/d, but the prorationing allowable lets it make ${Math.round(allow)} bbl/d — the rest slows its decline.`;
    }
    if (well.problem?.type === 'PROCESS_CAP') capNote += ' The platform’s process plant is already full, so output can’t rise until it has spare capacity.';
  } else well.stim = (well.stim ?? 1) * mult;
  msgs.push(`${well.name} shot: production ×${mult.toFixed(1)}.${capNote}`);
  const rig = wellRig(state, well);
  const crew = rig ? activeRigCrew(state, rig) : Object.values(state.crew).filter((c) => c.status === 'active' && !c.injuredUntil);
  const careful = traitCount(crew, 'careful') ? 0.7 : 1;
  if (chance(state, stat(def, 'accidentChance', 0.02) * careful)) {
    if (crew.length) injureCrew(state, crew[Math.floor(nextFloat(state) * crew.length)], 'was caught in a nitroglycerin accident', { cause: 'torpedo accident', fatalChance: 0.35, wellId: well.id, accident: 'torpedo' });
    if (rig) rig.condition = Math.max(0, rig.condition * 0.5);
    msgs.push('ACCIDENT: the nitro went off early!');
    notify(state, 'blowout', { wellId: well.id, fieldId: well.fieldId, pause: true, text: `${well.name}: torpedo accident! The shell exploded early.`, level: 'warn' });
  }
  if (!ownPatent && chance(state, stat(def, 'lawsuitChance', 0))) {
    const fine = eraDollars(state, stat(def, 'lawsuitFine', 500), def.baseYear ?? def.year);
    spend(state, fine, 'other', false);
    state.company.reputation = Math.max(0, state.company.reputation - 2);
    msgs.push(`Roberts Torpedo Co. sued for patent infringement: ${fmt$(fine)} fine.`);
    log(state, msgs[msgs.length - 1], 'warn', { wellId: well.id });
  }
  log(state, msgs[0], 'info', { wellId: well.id });
  return { ok: true, msg: msgs.join(' ') };
}

export { findDef };
